import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import type { PublicEvent } from "../types/public";
import type { PublicNoticesBundle } from "../types/site-notices";
import {
  KARIN_SUB_LABEL,
  formatDoorCurtainSlots,
  formatTimeRange,
  previewPerformanceOneLine,
  publicPerformersList,
  remoteKindLabel,
  weekdayJp,
} from "../types/public";
import { announceTimingBannersForRow } from "./announce-timing-banners";
import { adminListFilterMatchesRowWithVis, adminRowPassesDefaultEventDateFloor, type AdminOutletFilter } from "./admin-list-filter";
import { inferAdminModeForRow } from "./admin-event-prefill";
import { AdminCopyXFields } from "./AdminCopyXFields";
import { apiJson } from "./api";

type Row = Record<string, unknown>;

type NoticeItem = {
  noticeKey: string;
  block: string;
  title: string;
  eventId: string;
  event_name: string;
  event_date: string;
  venue: string | null;
  showTime: string | null;
  receptionEnd: string | null;
  label: string;
  linkUrl: string | null;
  autoReason: string;
  siteDisplay: string;
  syncState: string;
  hidden: boolean;
  sortOrder: number | null;
  labelOverride: string | null;
  linkOverride: string | null;
  parentEventId?: string;
  matchReason?: string;
};

type AdminBundle = {
  today: string;
  announcements: { today: string; events: Row[]; posts: Record<string, Record<string, string>> };
  publicNotices: PublicNoticesBundle;
  noticeManaging: { visible: NoticeItem[]; hidden: NoticeItem[] };
  promoting: Row[];
  unreleased: Row[];
  fanStatsByEvent?: Record<
    string,
    {
      notifyOn: number;
      applied: number;
      applicationSkip: number;
      going: number;
      stream: number;
      attendanceSkip: number;
      undecided: number;
    }
  >;
  stats: {
    noticeManagingVisible: number;
    noticeManagingHidden: number;
    promoting: number;
    unreleased: number;
    sitePending: number;
    siteFailed: number;
    unposted: number;
  };
};

type FanNotificationControlBundle = {
  dispatchPaused: boolean;
  dispatchPausedAt: string | null;
  summary: {
    notifyEnabledRows: number;
    pushSubscriptions: number;
  };
};

const UNLOCK_KEY = "admin_unreleased_unlock_v1";
const NOTICE_SCHEDULE_KEY = "admin_notice_schedule_times_v1";

type NoticeNotifySetting = {
  enabled: boolean;
  event: NoticeNotifyPlan;
  reception: NoticeNotifyPlan;
};

type NoticeNotifyPlan = {
  enabled: boolean;
  daysBefore: number;
  count: number;
  times: string[];
};

function asStr(v: unknown): string {
  return v == null ? "" : String(v);
}

function rowMatchesNoticeSearch(q: string, e: Row): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [
    asStr(e.event_name),
    asStr(e.event_date),
    asStr(e.venue),
    asStr(e.reception_period_text),
    asStr(e.detail_url),
    asStr(e.reserve_url),
    asStr(e.memo),
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function rowMatchesNoticeDate(date: string, e: Row): boolean {
  const d = date.trim();
  if (!d) return true;
  return asStr(e.event_date).slice(0, 10) === d || asStr(e.reception_start).slice(0, 10) === d || asStr(e.reception_end).slice(0, 10) === d;
}

function rowIsOnOrAfterDate(e: Row, date: string): boolean {
  const floor = date.trim();
  const eventDate = asStr(e.event_date).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(floor)) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) return false;
  return eventDate >= floor;
}

function noticeMatchesSearch(q: string, item: NoticeItem): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [
    item.label,
    item.event_name,
    item.event_date,
    item.venue ?? "",
    item.showTime ?? "",
    item.receptionEnd ?? "",
    item.linkUrl ?? "",
    item.autoReason,
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function noticeMatchesDate(date: string, item: NoticeItem): boolean {
  const d = date.trim();
  if (!d) return true;
  return item.event_date.slice(0, 10) === d || String(item.receptionEnd ?? "").slice(0, 10) === d;
}

function noticeFallbackRow(item: NoticeItem): Row {
  return {
    id: item.eventId,
    event_name: item.event_name,
    event_date: item.event_date,
    venue: item.venue,
    performance_text: item.showTime,
    time_start: item.showTime,
    reception_end: item.receptionEnd,
    detail_url: item.linkUrl,
    reserve_url: item.linkUrl,
    visibility: "public-notice",
    status: "ok",
    is_karin: 1,
    event_kind: "notice",
  };
}

function rowToPublicEvent(e: Row): PublicEvent {
  let performers: string[] = [];
  try {
    const parsed = JSON.parse(asStr(e.performers_json) || "[]") as unknown;
    performers = Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    performers = [];
  }
  return {
    id: asStr(e.id),
    source: asStr(e.source),
    event_kind: asStr(e.event_kind) || "event",
    event_name: asStr(e.event_name),
    venue: asStr(e.venue) || null,
    prefecture: asStr(e.prefecture) || null,
    event_date: asStr(e.event_date),
    time_start: asStr(e.time_start) || null,
    time_end: asStr(e.time_end) || null,
    door_time: asStr(e.door_time) || null,
    curtain_time: asStr(e.curtain_time) || null,
    door_time_2: asStr(e.door_time_2) || null,
    curtain_time_2: asStr(e.curtain_time_2) || null,
    door_time_3: asStr(e.door_time_3) || null,
    curtain_time_3: asStr(e.curtain_time_3) || null,
    performers,
    description: asStr(e.description) || null,
    detail_url: asStr(e.detail_url) || null,
    reserve_url: asStr(e.reserve_url) || null,
    memo: asStr(e.memo) || null,
    fan_group_name: asStr(e.fan_group_name) || null,
    fan_artist_name: asStr(e.fan_artist_name) || null,
    source_title: asStr(e.source_title) || null,
    performance_count: e.performance_count == null || e.performance_count === "" ? null : Number(e.performance_count),
    is_karin: Boolean(Number(e.is_karin)),
    karin_subtype: asStr(e.karin_subtype) || null,
    group_code: asStr(e.group_code) || null,
    reception_start: asStr(e.reception_start) || null,
    reception_end: asStr(e.reception_end) || null,
    reception_period_text: asStr(e.reception_period_text) || null,
    performance_text: asStr(e.performance_text) || null,
  };
}

function venueDisplayLine(e: PublicEvent): string {
  const pref = String(e.prefecture ?? "").trim();
  const v = String(e.venue ?? "").trim();
  if (pref && v) return `${pref} · ${v}`;
  if (v) return v;
  if (pref) return pref;
  return "会場未定";
}

function slotTimeSummaryLine(e: PublicEvent): string {
  const tr = formatTimeRange(e);
  const slots = formatDoorCurtainSlots(e);
  const bits: string[] = [];
  if (tr && tr !== "—") bits.push(`開始〜終了 ${tr}`);
  if (slots) bits.push(slots);
  return bits.join(" ｜ ") || "時間は未定または未入力です";
}

const NOTIFY_DAY_OPTIONS = Array.from({ length: 15 }, (_, i) => i);
const NOTIFY_COUNT_OPTIONS = [1, 2, 3, 4, 5];
const NOTIFY_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const NOTIFY_MINUTE_OPTIONS = ["00", "15", "30", "45"];

function defaultNotifyPlan(overrides: Partial<NoticeNotifyPlan> = {}): NoticeNotifyPlan {
  return {
    enabled: false,
    daysBefore: 1,
    count: 1,
    times: ["09:00"],
    ...overrides,
  };
}

function normalizeNotifyPlan(raw: unknown, fallback?: Partial<NoticeNotifyPlan>): NoticeNotifyPlan {
  const r = typeof raw === "object" && raw !== null ? (raw as Partial<NoticeNotifyPlan>) : {};
  const count = Math.max(1, Math.min(5, Number(r.count ?? fallback?.count ?? 1) || 1));
  const rawTimes = Array.isArray(r.times) ? r.times.map(String) : fallback?.times ?? ["09:00"];
  const times = Array.from({ length: count }, (_, i) => {
    const t = rawTimes[i] ?? rawTimes[0] ?? "09:00";
    return /^\d{2}:\d{2}$/.test(t) ? t : "09:00";
  });
  return defaultNotifyPlan({
    enabled: Boolean(r.enabled ?? fallback?.enabled ?? false),
    daysBefore: Math.max(0, Math.min(14, Number(r.daysBefore ?? fallback?.daysBefore ?? 1) || 0)),
    count,
    times,
  });
}

function normalizeNotifySetting(raw: unknown): NoticeNotifySetting {
  if (typeof raw === "string") {
    return {
      enabled: Boolean(raw),
      event: normalizeNotifyPlan(null, { enabled: Boolean(raw), daysBefore: 1, count: raw ? 1 : 1, times: raw ? [raw] : ["09:00"] }),
      reception: normalizeNotifyPlan(null, { enabled: false, daysBefore: 1, count: 1, times: ["09:00"] }),
    };
  }
  const r = typeof raw === "object" && raw !== null ? (raw as Partial<NoticeNotifySetting> & { count?: number; times?: string[] }) : {};
  if ("event" in r || "reception" in r) {
    const event = normalizeNotifyPlan(r.event);
    const reception = normalizeNotifyPlan(r.reception);
    return { enabled: Boolean(r.enabled ?? (event.enabled || reception.enabled)), event, reception };
  }
  const legacyPlan = normalizeNotifyPlan(null, {
    enabled: Boolean(r.enabled),
    count: Number(r.count) || 1,
    times: Array.isArray(r.times) ? r.times.map(String) : ["09:00"],
  });
  return {
    enabled: Boolean(r.enabled),
    event: legacyPlan,
    reception: normalizeNotifyPlan(null, { enabled: false, daysBefore: 1, count: 1, times: ["09:00"] }),
  };
}

function PublicLikeEventSummary({ e }: { e: Row }) {
  const pub = rowToPublicEvent(e);
  const kindLabel = remoteKindLabel(pub.event_kind);
  const doorSlots = formatDoorCurtainSlots(pub);
  const perfFull = String(pub.performance_text ?? "").trim();
  const perfOne = previewPerformanceOneLine(pub.performance_text, 220);
  const performers = publicPerformersList(pub);
  const description = String(pub.description ?? "").trim();
  const memo = String(pub.memo ?? "").trim();
  const sourceTitle = String(pub.source_title ?? "").trim();
  const fanGroup = String(pub.fan_group_name ?? "").trim();
  const fanArtist = String(pub.fan_artist_name ?? "").trim();
  return (
    <div className="karin-day-slot" style={{ marginTop: 8 }}>
      <div className="karin-slot-main">
        <h3 className="title karin-slot-title">{pub.event_name}</h3>
        <div className="meta">
          {pub.event_date ? `${pub.event_date}（${weekdayJp(pub.event_date)}）` : "開催日未定"}
        </div>
        <div className="karin-slot-venue-large">{venueDisplayLine(pub)}</div>
        <div className="karin-slot-times-collapsed">
          <span className="karin-slot-times-label">時間</span>
          <span className="karin-slot-times-text">{slotTimeSummaryLine(pub)}</span>
        </div>
        <div className="karin-slot-action">
          {pub.detail_url ? (
            <a className="btn small secondary" href={pub.detail_url} target="_blank" rel="noreferrer">
              詳細サイト
            </a>
          ) : (
            <span className="pill karin-slot-pill">これから公開だよ！</span>
          )}
        </div>
      </div>
      <details className="karin-slot-details">
        <summary>開く</summary>
        <div className="karin-slot-details-body">
          <p className="karin-detail-line">
            <span className="karin-detail-k">開催日</span> {pub.event_date ? `${pub.event_date}（${weekdayJp(pub.event_date)}）` : "—"}
          </p>
          <p className="karin-detail-line">
            <span className="karin-detail-k">会場</span> {venueDisplayLine(pub)}
          </p>
          <p className="karin-detail-line">
            <span className="karin-detail-k">出演者</span> {performers.length ? performers.join(" / ") : "—"}
          </p>
          {kindLabel ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">種別</span> {kindLabel}
            </p>
          ) : null}
          {pub.karin_subtype ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">タイプ</span> {KARIN_SUB_LABEL[pub.karin_subtype] ?? "その他"}
            </p>
          ) : null}
          {pub.prefecture ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">地域</span> {pub.prefecture}
            </p>
          ) : null}
          {formatTimeRange(pub) !== "—" ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">開始・終了</span> {formatTimeRange(pub)}
            </p>
          ) : null}
          {doorSlots ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">開場・開演</span> {doorSlots}
            </p>
          ) : null}
          {pub.reception_period_text || pub.reception_start || pub.reception_end ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">受付</span> {pub.reception_period_text || `${pub.reception_start ?? ""} 〜 ${pub.reception_end ?? ""}`}
            </p>
          ) : null}
          {perfFull ? (
            <p className="karin-detail-block">
              <span className="karin-detail-k">本番・メモ</span>
              <span className="karin-detail-pre">{perfFull}</span>
            </p>
          ) : perfOne ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">本番・メモ</span> {perfOne}
            </p>
          ) : null}
          {description ? (
            <p className="karin-detail-block">
              <span className="karin-detail-k">詳細</span>
              <span className="karin-detail-pre">{description}</span>
            </p>
          ) : null}
          {memo ? (
            <p className="karin-detail-block">
              <span className="karin-detail-k">メモ</span>
              <span className="karin-detail-pre">{memo}</span>
            </p>
          ) : null}
          {fanGroup || fanArtist || pub.performance_count != null || sourceTitle ? (
            <p className="karin-detail-block">
              <span className="karin-detail-k">投稿情報</span>
              <span className="karin-detail-pre">
                {fanGroup ? `グループ名: ${fanGroup}\n` : ""}
                {fanArtist ? `アーティスト名: ${fanArtist}\n` : ""}
                {pub.performance_count != null ? `公演数: ${pub.performance_count}\n` : ""}
                {sourceTitle ? `取得タイトル: ${sourceTitle}` : ""}
              </span>
            </p>
          ) : null}
          {pub.detail_url ? (
            <p className="karin-detail-line">
              <span className="karin-detail-k">詳細URL</span>{" "}
              <a href={pub.detail_url} target="_blank" rel="noreferrer">
                {pub.detail_url}
              </a>
            </p>
          ) : null}
          {pub.reserve_url ? (
            <p className="karin-detail-actions">
              <a className="btn small" href={pub.reserve_url} target="_blank" rel="noreferrer">
                予約サイト
              </a>
            </p>
          ) : null}
        </div>
      </details>
    </div>
  );
}

export function AnnouncementsPanel() {
  const adminCtx = useOutletContext<AdminOutletFilter | undefined>();
  const [searchParams] = useSearchParams();
  const adminEventDateFrom = adminCtx?.adminEventDateFrom ?? "";
  const adminEventDateTo = adminCtx?.adminEventDateTo ?? "";
  const adminKeyword = adminCtx?.adminKeyword ?? "";
  const adminKarinSubtype = adminCtx?.adminKarinSubtype ?? "all";
  const adminVis = adminCtx?.adminVis ?? "";

  const [data, setData] = useState<AdminBundle | null>(null);
  const [siteNotices, setSiteNotices] = useState<PublicNoticesBundle | null>(null);
  const [publicKarinRows, setPublicKarinRows] = useState<Row[]>([]);
  const [publicKarinLoading, setPublicKarinLoading] = useState(true);
  const [fanNotifyControl, setFanNotifyControl] = useState<FanNotificationControlBundle | null>(null);
  const [fanNotifyBusy, setFanNotifyBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(UNLOCK_KEY) === "1");
  const [showHiddenNotices, setShowHiddenNotices] = useState(true);
  const [noticeSearch, setNoticeSearch] = useState("");
  const [noticeDate, setNoticeDate] = useState("");
  const [notifySaveMsg, setNotifySaveMsg] = useState<Record<string, string>>({});
  const [notifySettings, setNotifySettings] = useState<Record<string, NoticeNotifySetting>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(NOTICE_SCHEDULE_KEY) ?? "{}") as Record<string, unknown>;
      const normalized: Record<string, NoticeNotifySetting> = {};
      for (const [key, value] of Object.entries(raw)) {
        normalized[key] = normalizeNotifySetting(value);
      }
      return normalized;
    } catch {
      return {};
    }
  });

  const updateNotifySetting = (noticeKey: string, patch: Partial<NoticeNotifySetting>) => {
    setNotifySaveMsg((prev) => {
      if (!prev[noticeKey]) return prev;
      const next = { ...prev };
      delete next[noticeKey];
      return next;
    });
    setNotifySettings((prev) => {
      const current = prev[noticeKey] ?? normalizeNotifySetting(null);
      const nextSetting: NoticeNotifySetting = {
        ...current,
        ...patch,
        enabled: Boolean(patch.enabled ?? current.enabled),
        event: normalizeNotifyPlan(patch.event ?? current.event),
        reception: normalizeNotifyPlan(patch.reception ?? current.reception),
      };
      nextSetting.enabled = nextSetting.event.enabled || nextSetting.reception.enabled;
      const next = { ...prev, [noticeKey]: nextSetting };
      if (!nextSetting.enabled) delete next[noticeKey];
      localStorage.setItem(NOTICE_SCHEDULE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const registerNotifySetting = (noticeKey: string) => {
    const current = notifySettings[noticeKey] ?? normalizeNotifySetting(null);
    const normalized = normalizeNotifySetting(current);
    const nextSettings = {
      ...notifySettings,
      [noticeKey]: normalized,
    };
    if (!normalized.event.enabled && !normalized.reception.enabled) {
      delete nextSettings[noticeKey];
    }
    localStorage.setItem(NOTICE_SCHEDULE_KEY, JSON.stringify(nextSettings));
    setNotifySettings(nextSettings);
    setNotifySaveMsg((prev) => ({ ...prev, [noticeKey]: "登録できました" }));
  };

  const TimeSelect = ({
    value,
    disabled,
    onChange,
  }: {
    value: string;
    disabled?: boolean;
    onChange: (next: string) => void;
  }) => {
    const [hour = "09", minute = "00"] = /^\d{2}:\d{2}$/.test(value) ? value.split(":") : ["09", "00"];
    return (
      <span className="row" style={{ gap: 4, flexWrap: "nowrap", alignItems: "center" }}>
        <select className="input" value={hour} disabled={disabled} onChange={(ev) => onChange(`${ev.target.value}:${minute}`)} style={{ width: 74 }}>
          {NOTIFY_HOUR_OPTIONS.map((h) => (
            <option key={h} value={h}>
              {h}時
            </option>
          ))}
        </select>
        <select className="input" value={minute} disabled={disabled} onChange={(ev) => onChange(`${hour}:${ev.target.value}`)} style={{ width: 74 }}>
          {NOTIFY_MINUTE_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}分
            </option>
          ))}
        </select>
      </span>
    );
  };

  const NoticePlanControls = ({
    label,
    targetDate,
    plan,
    disabled,
    onChange,
  }: {
    label: string;
    targetDate: string;
    plan: NoticeNotifyPlan;
    disabled?: boolean;
    onChange: (next: NoticeNotifyPlan) => void;
  }) => {
    const count = Math.max(1, Math.min(5, plan.count || 1));
    const times = Array.from({ length: count }, (_, i) => plan.times[i] ?? "09:00");
    return (
      <section className="na-notify-setting-subpanel">
        <label className="na-notify-setting-toggle">
          <input
            type="checkbox"
            checked={plan.enabled}
            disabled={disabled}
            onChange={(ev) => onChange(normalizeNotifyPlan({ ...plan, enabled: ev.target.checked, count, times }))}
          />
          {label}を通知する
        </label>
        <p className="meta" style={{ margin: "4px 0 8px", fontSize: 11 }}>
          対象日: {targetDate || "未設定"}
        </p>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="na-notify-count-field">
            <span>何日前から</span>
            <select
              className="input"
              value={plan.daysBefore}
              disabled={disabled || !plan.enabled}
              onChange={(ev) => onChange(normalizeNotifyPlan({ ...plan, daysBefore: Number(ev.target.value), count, times }))}
            >
              {NOTIFY_DAY_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n === 0 ? "当日だけ" : `${n}日前から`}
                </option>
              ))}
            </select>
          </label>
          <label className="na-notify-count-field">
            <span>1日何回</span>
            <select
              className="input"
              value={count}
              disabled={disabled || !plan.enabled}
              onChange={(ev) => {
                const nextCount = Number(ev.target.value);
                onChange(normalizeNotifyPlan({ ...plan, count: nextCount, times: Array.from({ length: nextCount }, (_, i) => times[i] ?? "09:00") }));
              }}
            >
              {NOTIFY_COUNT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}回
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="na-notify-times-grid">
          {times.map((time, idx) => (
            <label key={idx} className="na-notice-time-field">
              <span>{idx + 1}回目</span>
              <TimeSelect
                disabled={disabled || !plan.enabled}
                value={time}
                onChange={(nextTime) => {
                  const nextTimes = [...times];
                  nextTimes[idx] = nextTime;
                  onChange(normalizeNotifyPlan({ ...plan, count, times: nextTimes }));
                }}
              />
            </label>
          ))}
        </div>
      </section>
    );
  };

  const NoticeNotifyControls = ({ noticeKey, eventDate, receptionDate }: { noticeKey: string; eventDate?: string; receptionDate?: string | null }) => {
    const setting = notifySettings[noticeKey] ?? normalizeNotifySetting(null);
    const updatePlan = (key: "event" | "reception", plan: NoticeNotifyPlan) => {
      updateNotifySetting(noticeKey, { ...setting, [key]: plan });
    };
    const activePlans = [setting.event.enabled, setting.reception.enabled].filter(Boolean).length;
    const activeTimes = [...(setting.event.enabled ? setting.event.times.slice(0, setting.event.count) : []), ...(setting.reception.enabled ? setting.reception.times.slice(0, setting.reception.count) : [])].length;
    return (
      <div className="na-notify-setting-panel">
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          <strong>通知の詳細設定</strong>
          <span className="meta" style={{ fontSize: 11 }}>
            開催日・受付ごとに、日数/回数/時刻をピッカーで指定
          </span>
        </div>
        <NoticePlanControls
          label="開催日"
          targetDate={eventDate ?? ""}
          plan={setting.event}
          disabled={!eventDate}
          onChange={(plan) => updatePlan("event", plan)}
        />
        <NoticePlanControls
          label="受付締切"
          targetDate={receptionDate ?? ""}
          plan={setting.reception}
          disabled={!receptionDate}
          onChange={(plan) => updatePlan("reception", plan)}
        />
        <p className="meta" style={{ margin: "6px 0 0", fontSize: 11 }}>
          {activePlans ? `通知対象: ${activePlans}種類 / 1日あたり${activeTimes}時刻を設定中` : "通知しない設定です"}
        </p>
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" className="btn small" onClick={() => registerNotifySetting(noticeKey)}>
            通知設定を登録
          </button>
          {notifySaveMsg[noticeKey] ? (
            <span className="pill mono" style={{ color: "#2e7d32", borderColor: "rgba(46,125,50,0.35)", background: "rgba(232,245,233,0.9)" }}>
              {notifySaveMsg[noticeKey]}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const load = useCallback(async () => {
    setErr(null);
    try {
      const bundle = await apiJson<AdminBundle>("/api/admin/announcements");
      setData(bundle);
    } catch (e) {
      setErr(String(e));
    }
  }, []);

  const loadFanNotifyControl = useCallback(async () => {
    try {
      const bundle = await apiJson<FanNotificationControlBundle>("/api/admin/fan-notifications");
      setFanNotifyControl(bundle);
    } catch {
      setFanNotifyControl(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadFanNotifyControl();
  }, [load, loadFanNotifyControl]);

  useEffect(() => {
    const d = (searchParams.get("notice_date") ?? "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      setNoticeDate(d);
      window.setTimeout(() => {
        document.getElementById("notice-promoting-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [searchParams]);

  const toggleFanNotificationDelivery = async () => {
    const nextPaused = !fanNotifyControl?.dispatchPaused;
    const ok = confirm(
      nextPaused
        ? "ファンに届くPush通知を一斉にOFFにしますか？\nファン側の通知ON設定は残りますが、配信は止まります。"
        : "ファンに届くPush通知を一斉にONに戻しますか？",
    );
    if (!ok) return;
    setFanNotifyBusy(true);
    try {
      await apiJson("/api/admin/fan-notifications/pause", {
        method: "POST",
        body: JSON.stringify({ paused: nextPaused }),
      });
      setActionMsg(nextPaused ? "ファンに届く通知を一斉OFFにしました。" : "ファンに届く通知を一斉ONにしました。");
      await loadFanNotifyControl();
    } catch (e) {
      setErr(String(e));
    } finally {
      setFanNotifyBusy(false);
    }
  };


  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/public-data/notices.json", { cache: "no-store" });
        if (!res.ok) return;
        const json = (await res.json()) as PublicNoticesBundle;
        if (alive) setSiteNotices(json);
      } catch {
        // The admin bundle is still available as a fallback.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setPublicKarinLoading(true);
    (async () => {
      try {
        const res = await fetch("/public-data/karin.json", { cache: "no-store" });
        if (!res.ok) return;
        const json = (await res.json()) as { events?: PublicEvent[] };
        const rows = (json.events ?? []).map((e) => ({
          ...e,
          performers_json: JSON.stringify(e.performers ?? []),
        })) as Row[];
        if (alive) setPublicKarinRows(rows);
      } catch {
        // Admin bundle remains available as a fallback.
      } finally {
        if (alive) setPublicKarinLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filterRow = (e: Row) =>
    adminRowPassesDefaultEventDateFloor(undefined, adminEventDateFrom, adminEventDateTo, e) &&
    adminListFilterMatchesRowWithVis(adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, e, adminKarinSubtype) &&
    rowMatchesNoticeSearch(noticeSearch, e) &&
    rowMatchesNoticeDate(noticeDate, e);

  const promoting = useMemo(() => {
    const rows = publicKarinRows.length ? publicKarinRows : data?.promoting ?? [];
    return rows.filter((e) => rowIsOnOrAfterDate(e, data?.today ?? "") && rowMatchesNoticeSearch(noticeSearch, e) && rowMatchesNoticeDate(noticeDate, e));
  }, [data, publicKarinRows, noticeSearch, noticeDate]);
  const unreleased = useMemo(() => (data?.unreleased ?? []).filter(filterRow), [data, adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, adminKarinSubtype, noticeSearch, noticeDate]);

  const visibleUrgentNotices = useMemo(() => {
    if (!data) return [];
    return data.noticeManaging.visible.filter((n) => n.block === "urgent");
  }, [data]);

  const visibleReceptionNotices = useMemo(() => {
    if (!data) return [];
    return data.noticeManaging.visible.filter((n) => n.block === "reception");
  }, [data]);

  const visibleNearbyNotices = useMemo(() => {
    if (!data) return [];
    return data.noticeManaging.visible.filter((n) => n.block === "nearby");
  }, [data]);

  const eventsById = useMemo(() => {
    const m = new Map<string, Row>();
    for (const e of [...(data?.promoting ?? []), ...(data?.unreleased ?? []), ...(data?.announcements.events ?? [])]) {
      m.set(String(e.id), e);
    }
    return m;
  }, [data]);

  const calendarNoticeItems = useMemo(() => {
    const notices = siteNotices ?? data?.publicNotices;
    if (!notices) return [];
    const items: NoticeItem[] = [];
    for (const item of notices.urgent.items) {
      items.push({
        noticeKey: item.noticeKey,
        block: "urgent",
        title: item.label,
        eventId: item.eventId,
        event_name: item.event_name,
        event_date: item.event_date,
        venue: item.venue,
        showTime: item.showTime,
        receptionEnd: item.deadlineDate,
        label: item.label,
        linkUrl: item.linkUrl,
        autoReason: item.autoReason,
        siteDisplay: "visible",
        syncState: "表サイトJSON",
        hidden: false,
        sortOrder: null,
        labelOverride: null,
        linkOverride: null,
      });
    }
    for (const item of notices.reception.items) {
      items.push({
        noticeKey: item.noticeKey,
        block: "reception",
        title: "受付中",
        eventId: item.eventId,
        event_name: item.event_name,
        event_date: item.event_date,
        venue: item.venue,
        showTime: item.showTime,
        receptionEnd: item.receptionEnd,
        label: "受付中",
        linkUrl: item.linkUrl,
        autoReason: item.autoReason,
        siteDisplay: "visible",
        syncState: "表サイトJSON",
        hidden: false,
        sortOrder: null,
        labelOverride: null,
        linkOverride: null,
      });
    }
    return items.filter((item) => noticeMatchesSearch(noticeSearch, item) && noticeMatchesDate(noticeDate, item));
  }, [data, siteNotices, noticeSearch, noticeDate]);

  const calendarUrgentNotices = useMemo(() => calendarNoticeItems.filter((n) => n.block === "urgent"), [calendarNoticeItems]);
  const calendarReceptionNotices = useMemo(() => calendarNoticeItems.filter((n) => n.block === "reception"), [calendarNoticeItems]);

  const promotingWithCalendarNotices = useMemo(() => {
    const map = new Map<string, Row>();
    for (const e of promoting) map.set(String(e.id), e);
    for (const notice of calendarNoticeItems) {
      const base = eventsById.get(notice.eventId) ?? {
        id: notice.eventId,
        event_name: notice.event_name,
        event_date: notice.event_date,
        venue: notice.venue,
        curtain_time: notice.showTime,
        reception_end: notice.receptionEnd,
        detail_url: notice.linkUrl,
        visibility: "public-notice",
        status: "ok",
        is_karin: 1,
        event_kind: "notice",
      };
      const matches =
        !Number(base.is_karin) ? false : true;
      if (matches && rowIsOnOrAfterDate(base, data?.today ?? "")) map.set(notice.eventId, base);
    }
    return [...map.values()];
  }, [data, promoting, calendarNoticeItems, eventsById, adminKeyword, adminKarinSubtype, noticeSearch, noticeDate]);

  const patchNotice = async (noticeKey: string, patch: { hidden?: boolean; labelOverride?: string; linkOverride?: string }) => {
    try {
      await apiJson("/api/admin/site-notices/override", { method: "PATCH", body: JSON.stringify({ noticeKey, ...patch }) });
      setActionMsg(patch.hidden === true ? "非表示にしました。" : patch.hidden === false ? "表示しました。" : "お知らせ設定を保存しました。");
      await load();
    } catch (e) {
      setErr(String(e));
    }
  };

  const regenerate = async () => {
    try {
      setActionMsg("お知らせを再生成中です…");
      await apiJson("/api/admin/site-notices/regenerate", { method: "POST", body: "{}" });
      setActionMsg("お知らせを再生成しました。");
      await load();
    } catch (e) {
      setErr(String(e));
      setActionMsg("再生成に失敗しました。");
    }
  };

  const NoticeCard = ({ item, hiddenView }: { item: NoticeItem; hiddenView?: boolean }) => {
    const e = eventsById.get(item.eventId) ?? noticeFallbackRow(item);
    return (
      <div className={`na-notice-card card${hiddenView ? " na-notice-card--hidden" : ""}`} style={{ boxShadow: "none" }}>
        <div className="na-mobile-card-head">
          <div className="na-mobile-card-main">
            <div className="title na-mobile-card-title">
              {item.label} — {item.event_name}
            </div>
            <div className="na-mobile-card-meta">
              <span className="mono">{item.event_date}</span>
              {item.showTime ? <span>{item.showTime}</span> : null}
              {item.receptionEnd ? <span>締切 {item.receptionEnd}</span> : null}
            </div>
            <div className="na-status-row">
              <strong>表サイト:</strong> {hiddenView ? "非表示" : "表示中"} ・ <strong>反映:</strong> {item.syncState}
            </div>
            <div className="meta mono na-mobile-card-reason">
              {item.autoReason}
            </div>
          </div>
          <div className="na-mobile-card-actions">
            <button type="button" className="btn small secondary" onClick={() => void patchNotice(item.noticeKey, { hidden: !hiddenView })}>
              {hiddenView ? "表示する" : "非表示"}
            </button>
            <Link className="btn small secondary" to={`/admin/events/all?edit=${encodeURIComponent(item.eventId)}`}>
              予定管理で開く
            </Link>
          </div>
        </div>
        <details className="na-card-details">
          <summary>詳細・通知・コピーを開く</summary>
          <div className="na-card-details-body">
            <div className="row na-card-secondary-actions">
              <Link className="btn small secondary" to="/" target="_blank">
                表サイトで確認
              </Link>
              <button type="button" className="btn small secondary" onClick={() => void regenerate()}>
                再生成
              </button>
            </div>
            <dl className="announce-event-dl">
              <dt>開催日</dt>
              <dd className="mono">{item.event_date}</dd>
              <dt>会場</dt>
              <dd>{item.venue || "—"}</dd>
              <dt>開演</dt>
              <dd>{item.showTime || "—"}</dd>
              {item.receptionEnd ? (
                <>
                  <dt>締切</dt>
                  <dd className="mono">{item.receptionEnd}</dd>
                </>
              ) : null}
              <dt>リンク</dt>
              <dd className="mono">{item.linkUrl || "—"}</dd>
            </dl>
            <details className="na-inner-details">
              <summary>通知設定を開く</summary>
              <NoticeNotifyControls noticeKey={item.noticeKey} eventDate={item.event_date} receptionDate={item.receptionEnd} />
            </details>
            <details className="na-inner-details">
              <summary>X投稿コピー欄を開く</summary>
              <AdminCopyXFields e={e} />
            </details>
          </div>
        </details>
      </div>
    );
  };

  const EventCard = ({ e }: { e: Row }) => {
    const timingBanners = data ? announceTimingBannersForRow(data.today, e) : [];
    const onPublic = data?.noticeManaging.visible.some((n) => n.eventId === String(e.id)) || calendarNoticeItems.some((n) => n.eventId === String(e.id));
    const scheduleKey = `event:${String(e.id)}`;
    const stats = data?.fanStatsByEvent?.[String(e.id)] ?? null;
    const hasStats = Boolean(stats && (stats.notifyOn || stats.applied || stats.applicationSkip || stats.going || stats.stream || stats.attendanceSkip || stats.undecided));
    const statsSummary = hasStats && stats
      ? `通知ON ${stats.notifyOn} / 申込 ${stats.applied} / 行く ${stats.going} / 配信 ${stats.stream}`
      : "ファン反応なし";

    return (
      <div className="card na-event-card" style={{ marginTop: 10, boxShadow: "none" }}>
        {timingBanners.map((b) => (
          <div key={b.kind} className="announce-timing-banner" role="status">
            {b.message}
          </div>
        ))}
        <div className="na-mobile-card-head">
          <div className="na-mobile-card-main">
            <div className="na-status-row">
              表サイトお知らせ: <strong>{onPublic ? "表示中" : "—"}</strong> ・ 告知: <strong>告知中</strong>
            </div>
            <div className="title na-mobile-card-title">{asStr(e.event_name) || "（無題）"}</div>
            <div className="na-mobile-card-meta">
              <span className="mono">{asStr(e.event_date).slice(0, 10) || "開催日未定"}</span>
              {asStr(e.reception_end) ? <span>締切 {asStr(e.reception_end).slice(0, 10)}</span> : null}
            </div>
            <div className="meta na-mobile-card-stats">{statsSummary}</div>
          </div>
          <div className="na-mobile-card-actions">
            <Link
              className="btn small secondary"
              to={`/admin/events/${inferAdminModeForRow({ is_karin: 1, source: "manual", event_kind: e.event_kind })}?edit=${encodeURIComponent(String(e.id))}`}
            >
              予定を編集
            </Link>
          </div>
        </div>
        <details className="na-card-details">
          <summary>詳細・通知・コピーを開く</summary>
          <div className="na-card-details-body">
            <PublicLikeEventSummary e={e} />
            <details className="na-inner-details">
              <summary>通知設定を開く</summary>
              <NoticeNotifyControls noticeKey={scheduleKey} eventDate={asStr(e.event_date).slice(0, 10)} receptionDate={asStr(e.reception_end).slice(0, 10)} />
            </details>
            <details className="na-inner-details">
              <summary>X投稿コピー欄を開く</summary>
              <AdminCopyXFields e={e} />
            </details>
          </div>
        </details>
      </div>
    );
  };

  return (
    <>
      <header className="card" style={{ marginBottom: 12 }}>
        <section
          className="card"
          style={{
            margin: "0 0 12px",
            padding: "12px 14px",
            boxShadow: "none",
            borderColor: fanNotifyControl?.dispatchPaused ? "#f3b3c7" : "#b9ead1",
            background: fanNotifyControl?.dispatchPaused ? "#fff5f8" : "#f2fff8",
          }}
        >
          <div className="row" style={{ justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 900 }}>ファンに届く通知</div>
              <p className="meta" style={{ margin: "4px 0 0", lineHeight: 1.5 }}>
                現在: <strong>{fanNotifyControl?.dispatchPaused ? "一斉OFF" : "一斉ON"}</strong>
                {fanNotifyControl ? ` ・ 通知ON ${fanNotifyControl.summary.notifyEnabledRows}件 ・ 端末 ${fanNotifyControl.summary.pushSubscriptions}件` : ""}
              </p>
            </div>
            <button
              type="button"
              className={`btn small${fanNotifyControl?.dispatchPaused ? "" : " secondary"}`}
              style={fanNotifyControl?.dispatchPaused ? undefined : { borderColor: "#b00020", color: "#b00020" }}
              disabled={fanNotifyBusy || !fanNotifyControl}
              onClick={() => void toggleFanNotificationDelivery()}
            >
              {fanNotifyBusy ? "切替中…" : fanNotifyControl?.dispatchPaused ? "ファン通知を一斉ON" : "ファン通知を一斉OFF"}
            </button>
          </div>
        </section>
        <h1 style={{ margin: "0 0 8px", fontSize: 20 }}>お知らせ管理</h1>
        <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", lineHeight: 1.55 }}>
          表サイトのお知らせ・ファン向け通知の反応をここでまとめて管理します。ファンが「通知ON」「申込完了」「行く」などを押すと、下の件数に反映されます。
        </p>
        {data ? (
          <div className="na-admin-stats" style={{ marginTop: 12 }}>
            <span className="na-admin-stat-pill">お知らせ管理中 {data.stats.noticeManagingVisible}</span>
            <span className="na-admin-stat-pill">非表示 {data.stats.noticeManagingHidden}</span>
            <span className="na-admin-stat-pill">告知中 {data.stats.promoting}</span>
            <span className="na-admin-stat-pill">未解禁 {data.stats.unreleased}</span>
            <span className="na-admin-stat-pill">反映待ち {data.stats.sitePending}</span>
            <span className="na-admin-stat-pill">反映失敗 {data.stats.siteFailed}</span>
          </div>
        ) : null}
        <div className="row" style={{ marginTop: 10, gap: 8 }}>
          <button type="button" className="btn small" onClick={() => void regenerate()}>
            お知らせを再生成
          </button>
          <button type="button" className="btn small secondary" onClick={() => void load()}>
            再読み込み
          </button>
        </div>
      </header>

      {err ? <p style={{ color: "#b00020" }}>{err}</p> : null}
      {actionMsg ? (
        <p className="admin-action-toast" role="status">
          {actionMsg}
        </p>
      ) : null}
      {!data && !err ? <p className="meta">読み込み中…</p> : null}

      {data ? (
        <>
          <nav className="notice-workbench-toolbar card" aria-label="お知らせ作業ナビ">
            <div className="notice-workbench-search">
              <label>
                <span>告知検索</span>
                <input
                  className="input"
                  placeholder="イベント名・日付・会場・URLで検索"
                  value={noticeSearch}
                  onChange={(ev) => setNoticeSearch(ev.target.value)}
                />
              </label>
              <label className="notice-workbench-date-field">
                <span>日付</span>
                <input
                  className="input"
                  type="date"
                  value={noticeDate}
                  onChange={(ev) => setNoticeDate(ev.target.value)}
                />
              </label>
              {noticeSearch || noticeDate ? (
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    setNoticeSearch("");
                    setNoticeDate("");
                  }}
                >
                  検索クリア
                </button>
              ) : null}
            </div>
            <div className="notice-workbench-jumps">
              <a className="btn small secondary" href="#notice-calendar-section">
                カレンダーお知らせ
              </a>
              <a className="btn small secondary" href="#notice-promoting-section">
                告知中
              </a>
            </div>
          </nav>

          <section id="notice-calendar-section" className="na-section na-section--managing card notice-scroll-section" style={{ boxShadow: "none" }}>
            <h2 className="na-section-title">1. カレンダー上のお知らせ（表サイトと同じ）</h2>
            <p className="announce-section-lead">
              表サイトの「まもなく締切・まもなく開催」「本日開催」「現在受付中」に出る内容です（公開イベントのみ自動算出）。非表示や文言の上書きもここから行えます。
            </p>
            <label className="chip" style={{ marginBottom: 8 }}>
              <input type="checkbox" checked={showHiddenNotices} onChange={(ev) => setShowHiddenNotices(ev.target.checked)} /> 非表示中も表示
            </label>
            {calendarNoticeItems.length === 0 ? <p className="meta">表示中のお知らせはありません</p> : null}
            {calendarUrgentNotices.length > 0 ? (
              <>
                <h3 className="na-subsection-title">まもなく締切・まもなく開催</h3>
                {calendarUrgentNotices.map((item) => (
                  <NoticeCard key={item.noticeKey} item={item} />
                ))}
              </>
            ) : null}
            {calendarReceptionNotices.length > 0 ? (
              <>
                <h3 className="na-subsection-title">現在受付中</h3>
                {calendarReceptionNotices.map((item) => (
                  <NoticeCard key={item.noticeKey} item={item} />
                ))}
              </>
            ) : null}
            {visibleNearbyNotices.length > 0 ? (
              <>
                <h3 className="na-subsection-title">近くで開催中（関連イベント）</h3>
                {visibleNearbyNotices.map((item) => (
                  <NoticeCard key={item.noticeKey} item={item} />
                ))}
              </>
            ) : null}
            {showHiddenNotices
              ? data.noticeManaging.hidden.map((item) => <NoticeCard key={item.noticeKey} item={item} hiddenView />)
              : null}
          </section>

          <section id="notice-promoting-section" className="na-section na-section--promoting card notice-scroll-section" style={{ boxShadow: "none", marginTop: 14 }}>
            <h2 className="na-section-title">
              2. 告知中（宮本佳林・今日以降 {noticeDate ? `${noticeDate}：` : "全"}{promotingWithCalendarNotices.length}件）
            </h2>
            <p className="announce-section-lead">
              {noticeDate ? `${noticeDate} の告知対象予定を表示しています。` : "宮本佳林の公開中予定から、当日以降のものだけ表示します。"}
            </p>
            {promotingWithCalendarNotices.length === 0 ? (
              <p className="meta">{publicKarinLoading ? "読み込み中…" : "なし"}</p>
            ) : (
              promotingWithCalendarNotices.map((e) => <EventCard key={String(e.id)} e={e} />)
            )}
          </section>

          <section className="na-section na-section--unreleased card notice-scroll-section notice-scroll-section--small" style={{ boxShadow: "none", marginTop: 14 }}>
            <h2 className="na-section-title">3. 未解禁</h2>
            {!unlocked ? (
              <div>
                <p className="meta">未解禁の内容は表示前に確認が必要です。</p>
                <button
                  type="button"
                  className="btn small"
                  onClick={() => {
                    sessionStorage.setItem(UNLOCK_KEY, "1");
                    setUnlocked(true);
                  }}
                >
                  未解禁を表示
                </button>
              </div>
            ) : (
              <>
                <div className="row" style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    className="btn small secondary"
                    onClick={() => {
                      sessionStorage.removeItem(UNLOCK_KEY);
                      setUnlocked(false);
                    }}
                  >
                    閉じる（再ロック）
                  </button>
                </div>
                {unreleased.length === 0 ? <p className="meta">なし</p> : unreleased.map((e) => <EventCard key={String(e.id)} e={e} />)}
              </>
            )}
          </section>

        </>
      ) : null}
    </>
  );
}
